import React, { useCallback, useEffect, useState } from "react";

import { debounce } from "lodash";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { resetBuggyCounts, getBuggyCountData, approveBuggyCount, transferBuggyCount } from "api/inventory";

import { Checkbox, InputContainer, TextInput } from "components/Form";
import DefaultTable from "components/Table";
import PageTitle from "components/PageTitle";
import Widget from "components/Widget";
import { Button } from "components/Wrappers/Wrappers";

import { useLayoutDispatch, popupNotification } from "context/LayoutContext";

import { Check, FormatListNumbered } from "icons";

import { initialAPIListValue } from "utils/constants";
import getClearAdornment from "utils/form";

export default function BuggyCountGroup() {
  const [t] = useTranslation();
  const [groups, setGroups] = useState(initialAPIListValue);
  const [params, setParams] = useState({
    buggy: "",
    hide_empty: true,
  });
  const [itemModal, setItemModal] = useState(false);
  const [resetBuggyID, setResetBuggyID] = useState(null);
  const [approveBuggyID, setApproveBuggyID] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orderBy, setOrderBy] = useState("id");
  const [orderDirection, setOrderDirection] = useState("asc");

  const layoutDispatch = useLayoutDispatch();

  const headers = [
    {
      id: "buggy",
      render: (r) => r.id,
      sortable: true,
    },
    {
      id: "sku_count",
      label: t("SKU"),
      render: (r) => (r?.products ? Object.keys(r.products).length : 0),
      sortable: true,
    },
    { id: "total_qty", label: t("Qty"), sortable: true },
    {
      id: "actions",
      export: false,
      render: (r) => (
        <Grid container spacing={2}>
          {r.total_qty ? (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setItemModal(r);
                  }}
                >
                  List
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setResetBuggyID(r.id);
                  }}
                  disabled={!r.is_completed}
                >
                  Reset Location
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setApproveBuggyID(r.id);
                  }}
                  disabled={!r.is_completed}
                >
                  Approve
                </Button>
              </Grid>
            </>
          ) : null}
        </Grid>
      ),
    },
  ];

  useEffect(() => {
    if (groups.isFetching) {
      getBuggyCountData({
        params,
        responseSetter: (res) => {
          const { results } = res;
          setGroups({ isFetching: false, items: results, count: res.count });
        },
      });
    }
  }, [params, groups.isFetching]);

  const handleChangeFilterInput = (prop) => (e) => {
    setParams({ ...params, [prop]: e.target.value });
    debouncedSearchHandler();
  };

  const handleChangeCheckbox = (prop) => (event) => {
    setParams({ ...params, [prop]: event.target.checked || null });
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setParams({ ...params, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, params);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setGroups({ ...groups, isFetching: true });
    }, 500),
    [],
  );

  return (
    <>
      <PageTitle title={t("buggy_counts")} />
      <Widget>
        <InputContainer>
          <Grid container>
            <Grid item lg={2} md={4} sm={6} xs={12}>
              <TextInput
                fullWidth
                variant="outlined"
                id="location"
                label="Location"
                value={params.buggy}
                onChange={handleChangeFilterInput("buggy")}
                InputProps={clearAdornment("buggy")}
                LeftIcon={FormatListNumbered}
              />
            </Grid>
            <Grid item lg={2} md={4} sm={6} xs={12}>
              <Checkbox
                checked={params.hide_empty}
                label="Hide Empty"
                onChange={handleChangeCheckbox("hide_empty")}
              />
            </Grid>
            <Box
              component={Grid}
              item
              lg={6}
              display={{
                xs: "none",
                sm: "none",
                md: "none",
                lg: "block",
              }}
              style={{ borderRight: "none" }}
            />
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => setApproveBuggyID("ALL")}
                disabled={groups.items.length === 0 || isLoading}
              >
                <Check color="white" />{" "}
                <span style={{ marginLeft: 5 }}>Approve All</span>
              </Button>
            </Grid>
          </Grid>
        </InputContainer>
        <Grid container spacing={4}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <DefaultTable
              headers={headers}
              data={groups}
              filters={params}
              exportDisabled={true}
              sortActions={{
                onSort: (header) => {
                  const isAsc = orderBy === header && orderDirection === "asc";
                  const newDirection = isAsc ? "desc" : "asc";
                  setOrderDirection(newDirection);
                  setOrderBy(header);
                  setParams({
                    ...params,
                    ordering: `${newDirection === "desc" ? "-" : ""}${header}`,
                  });
                  setGroups((prev) => ({ ...prev, isFetching: true }));
                },
                orderBy,
                orderDirection,
              }}
            />
          </Grid>
        </Grid>
        {resetBuggyID && (
          <Dialog
            open={true}
            onClose={() => setResetBuggyID(null)}
            aria-labelledby="confirm-dialog"
            maxWidth="lg"
          >
            <DialogTitle id="confirm-dialog">{resetBuggyID} Reset</DialogTitle>
            <DialogContent>
              <b>{resetBuggyID}</b> items will be reset. Are you sure?
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={() => setResetBuggyID(null)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const buggyId = resetBuggyID;

                  setIsLoading(true);

                  if (!buggyId) {
                    setIsLoading(false);
                    return;
                  }

                  resetBuggyCounts({
                    params: { buggy: buggyId },
                    responseSetter: (res) => {
                      setResetBuggyID(null);
                      if (res?.success) {
                        setIsLoading(false);
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: "Successful operation",
                          status: "success",
                        });
                        getBuggyCountData({
                          params,
                          responseSetter: (res) => {
                            const { results } = res;
                            setGroups({
                              isFetching: false,
                              items: results,
                              count: res.count,
                            });
                          },
                        });
                      }
                    },
                  });
                }}
                color="primary"
                disabled={isLoading}
              >
                {isLoading && <CircularProgress size={20} />}
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {approveBuggyID && (
          <Dialog
            open={true}
            onClose={() => setApproveBuggyID(null)}
            aria-labelledby="confirm-dialog"
            maxWidth="lg"
          >
            <DialogTitle id="confirm-dialog">
              {approveBuggyID === "ALL"
                ? "Approve All"
                : approveBuggyID + " Approve"}
            </DialogTitle>
            <DialogContent>
              {approveBuggyID === "ALL"
                ? "All locations will be approved and updated. Are you sure?"
                : approveBuggyID + " location will be updated. Are you sure?"}
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                disabled={isLoading}
                onClick={() => setApproveBuggyID(null)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const buggyId = approveBuggyID;

                  setIsLoading(true);

                  if (!buggyId) {
                    setIsLoading(false);
                    return;
                  }

                  approveBuggyCount({
                    body: { buggy: buggyId },
                    responseSetter: (res) => {
                      setIsLoading(false);
                      setApproveBuggyID(null);
                      if (res?.success) {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: "Successful operation",
                          status: "success",
                        });
                        getBuggyCountData({
                          params,
                          responseSetter: (res) => {
                            const { results } = res;
                            setGroups({
                              isFetching: false,
                              items: results,
                              count: res.count,
                            });
                          },
                        });
                      } else {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: "Operation failed",
                          status: "error",
                        });
                      }
                    },
                  });
                }}
                color="primary"
                disabled={isLoading}
              >
                {isLoading && <CircularProgress size={20} />}
                Approve
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const buggyId = approveBuggyID;

                  setIsLoading(true);

                  if (!buggyId) {
                    setIsLoading(false);
                    return;
                  }

                  approveBuggyCount({
                    body: { buggy: buggyId, transfer: true },
                    responseSetter: (res) => {
                      setIsLoading(false);
                      setApproveBuggyID(null);
                      if (res?.success) {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: "Successful operation",
                          status: "success",
                        });
                        getBuggyCountData({
                          params,
                          responseSetter: (res) => {
                            const { results } = res;
                            setGroups({
                              isFetching: false,
                              items: results,
                              count: res.count,
                            });
                          },
                        });
                      } else {
                        popupNotification({
                          dispatch: layoutDispatch,
                          message: "Operation failed",
                          status: "error",
                        });
                      }
                    },
                  });
                }}
                color="secondary"
                disabled={isLoading}
              >
                {isLoading && <CircularProgress size={20} />}
                Transfer (OBT Dock)
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <Dialog
          open={itemModal}
          onClose={() => setItemModal(null)}
          aria-labelledby="item-dialog"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle id="item-dialog">{`${itemModal?.id} (${itemModal?.total_qty})`}</DialogTitle>
          <DialogContent>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              {itemModal?.products &&
                Object.entries(itemModal?.products || {}).map(([sku, qty]) => (
                  <div
                    key={sku}
                    style={{
                      backgroundColor: "#f0f0f0",
                      margin: 5,
                      padding: 5,
                      paddingLeft: 10,
                      paddingRight: 10,
                      borderRadius: 4,
                    }}
                  >
                    <b>{sku}</b>: <span style={{ float: "right" }}>{qty}</span>
                  </div>
                ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={isLoading}
              onClick={() => setItemModal(null)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Widget>
    </>
  );
}
